import React, { useState } from 'react';
import { Collapse, Card, Typography, Badge, Tag, Tooltip, Space, Popconfirm, Tree, Empty, Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileLines, 
  faPlus, 
  faPen, 
  faTrash, 
  faCopy, 
  faBars, 
  faLock, 
  faSave, 
  faXmark 
} from '@fortawesome/free-solid-svg-icons';
import { AntdButton, InputField } from '../../blueplanitUtils/InputField';

const { Panel } = Collapse;
const { Text, Title } = Typography;

const DropdownItemList = ({
  pages,
  isDeveloperMode,
  isActionAllowed,
  expandedKeys,
  setExpandedKeys,
  expandedId,
  setExpandedId,
  copyingLoading,
  deletingLoading,
  editingLoading,
  editableNode,
  editingNodeData,
  setEditingNodeData,
  onCopyToClipboard,
  onOpenModal,
  onEditNode,
  onSaveNodeEdit,
  onCancelEdit,
  onDeleteItem
}) => {
  const [activePages, setActivePages] = useState([]);

  const renderTreeNodes = (data) =>
    data.map((item) => ({
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
          {editableNode === item._id ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <InputField
                size="small"
                value={editingNodeData.name}
                onChange={(e) => setEditingNodeData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Item name"
                style={{ width: 150 }}
              />
              <AntdButton
                type="primary"
                size="small"
                icon={editingLoading === item._id ? <Spin size="small" /> : <FontAwesomeIcon icon={faSave} />}
                onClick={() => onSaveNodeEdit(item)}
                loading={editingLoading === item._id}
              />
              <AntdButton
                size="small"
                icon={<FontAwesomeIcon icon={faXmark} />}
                onClick={onCancelEdit}
                disabled={editingLoading === item._id}
              />
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <span
                style={{
                  fontWeight: 'bold',
                  fontSize: '13px',
                  flex: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 120,
                  display: 'inline-block',
                  verticalAlign: 'middle'
                }}
              >
                {item.name || 'Unnamed Item'}
              </span>
              <Tag size="small" color="blue">{item.bucketLevel}</Tag>

              {/* Developer Mode: Show ID on hover */}
              {isDeveloperMode && (
                <Tooltip title={`Click to copy ID: ${item._id}`}>
                  <Tag
                    size="small"
                    color="purple"
                    style={{
                      cursor: 'pointer',
                      fontSize: '10px',
                      maxWidth: expandedId === item._id ? 'none' : '80px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      transition: 'all 0.3s'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (expandedId === item._id) {
                        onCopyToClipboard(item._id, item.bucketLevel, e);
                      } else {
                        setExpandedId(item._id);
                      }
                    }}
                    icon={copyingLoading === item._id ? <Spin size="small" /> : <FontAwesomeIcon icon={faCopy} />}
                  >
                    {copyingLoading === item._id ? 'Copying...' : 
                     expandedId === item._id ? item._id : item._id.slice(-6)}
                  </Tag>
                </Tooltip>
              )}

              <div>
                <Space size="small">
                  <Tooltip title={!isActionAllowed(`edit-${item.bucketLevel}`) ? "Developer mode required" : "Edit"}>
                    <AntdButton
                      type="link"
                      size="small"
                      icon={<FontAwesomeIcon icon={faPen} />}
                      onClick={() => onEditNode(item)}
                      style={{ padding: 0 }}
                      disabled={!isActionAllowed(`edit-${item.bucketLevel}`)}
                    />
                  </Tooltip>
                  <Tooltip title="Add Sub-option">
                    <AntdButton
                      type="link"
                      size="small"
                      icon={<FontAwesomeIcon icon={faPlus} />}
                      onClick={() => onOpenModal('option', item._id)}
                      style={{ padding: 0, color: '#52c41a' }}
                    />
                  </Tooltip>
                  <Tooltip title={!isActionAllowed(`delete-${item.bucketLevel}`) ? "Developer mode required" : "Delete"}>
                    <Popconfirm
                      title="Delete this item?"
                      onConfirm={() => onDeleteItem(item._id, item.bucketLevel)}
                      okText="Yes"
                      cancelText="No"
                      disabled={!isActionAllowed(`delete-${item.bucketLevel}`)}
                    >
                      <AntdButton
                        type="link"
                        size="small"
                        icon={deletingLoading === item._id ? <Spin size="small" /> : <FontAwesomeIcon icon={faTrash} />}
                        style={{ padding: 0, color: !isActionAllowed(`delete-${item.bucketLevel}`) ? '#d9d9d9' : '#ff4d4f' }}
                        disabled={!isActionAllowed(`delete-${item.bucketLevel}`) || deletingLoading === item._id}
                        loading={deletingLoading === item._id}
                      />
                    </Popconfirm>
                  </Tooltip>
                </Space>
              </div>
            </div>
          )}
        </div>
      ),
      key: item._id,
      children: item.children && item.children.length > 0 ? renderTreeNodes(item.children) : [],
    }));

  if (pages.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <FontAwesomeIcon icon={faFileLines} style={{ fontSize: '64px', color: '#999' }} />
          <Title level={3} type="secondary">No pages available</Title>
          <p>Create your first page to start building cascading dropdowns</p>
          {isActionAllowed('create-page') ? (
            <AntdButton type="primary" size="large" onClick={() => onOpenModal('page')} label={"+"}>
              
            </AntdButton>
          ) : (
            <Text type="secondary">
              <FontAwesomeIcon icon={faLock} /> Switch to developer mode to create pages
            </Text>
          )}
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Collapse
        onChange={(keys) => setActivePages(keys)}
        expandIconPosition="end"
      >
        {pages.map((page) => (
          <Panel
            key={page._id}
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {editableNode === page._id ? (
                  // Editing mode for page
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                    <FontAwesomeIcon icon={faFileLines} style={{ color: '#1890ff' }} />
                    <InputField
                      size="small"
                      value={editingNodeData.name}
                      onChange={(e) => setEditingNodeData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Page name"
                      style={{ width: 200 }}
                      onClick={(e) => e.stopPropagation()} // Prevent collapse toggle
                    />
                    <AntdButton
                      type="primary"
                      size="small"
                      icon={editingLoading === page._id ? <Spin size="small" /> : <FontAwesomeIcon icon={faSave} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSaveNodeEdit(page);
                      }}
                      loading={editingLoading === page._id}
                    />
                    <AntdButton
                      size="small"
                      icon={<FontAwesomeIcon icon={faXmark} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCancelEdit();
                      }}
                      disabled={editingLoading === page._id}
                    />
                  </div>
                ) : (
                  // Display mode for page
                  <>
                    <FontAwesomeIcon icon={faFileLines} style={{ color: '#1890ff' }} />
                    <span>{page.name}</span>
                    <Badge count={page.children?.length || 0} showZero />
                    {isDeveloperMode && (
                      <Tooltip title={`Page ID: ${page._id}`}>
                        <Tag
                          color="purple"
                          style={{
                            cursor: 'pointer',
                            fontSize: '10px',
                            maxWidth: expandedId === page._id ? 'none' : '80px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            transition: 'all 0.3s'
                          }}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent accordion expansion
                            if (expandedId === page._id) {
                              onCopyToClipboard(page._id, 'Page', e);
                            } else {
                              setExpandedId(page._id);
                            }
                          }}
                          icon={copyingLoading === page._id ? <Spin size="small" /> : <FontAwesomeIcon icon={faCopy} />}
                        >
                          {copyingLoading === page._id ? 'Copying...' : 
                           expandedId === page._id ? page._id : page._id.slice(-6)}
                        </Tag>
                      </Tooltip>
                    )}
                    <Space style={{ marginLeft: 'auto' }}>
                      <Tooltip title={!isActionAllowed('create-dropdown') ? "Developer mode required" : "Add Dropdown"}>
                        <AntdButton
                          type="link"
                          size="small"
                          icon={<FontAwesomeIcon icon={faPlus} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenModal('dropdown', page._id);
                          }}
                          style={{ color: isActionAllowed('create-dropdown') ? '#52c41a' : '#d9d9d9' }}
                          disabled={!isActionAllowed('create-dropdown')}
                        />
                      </Tooltip>
                      <Tooltip title={!isActionAllowed('edit-page') ? "Developer mode required" : "Edit Page"}>
                        <AntdButton
                          type="link"
                          size="small"
                          icon={<FontAwesomeIcon icon={faPen} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditNode(page);
                          }}
                          disabled={!isActionAllowed('edit-page')}
                        />
                      </Tooltip>
                      <Tooltip title={!isActionAllowed('delete-page') ? "Developer mode required" : "Delete Page"}>
                        <Popconfirm
                          title="Delete this page and all its dropdowns & options?"
                          onConfirm={(e) => {
                            e?.stopPropagation();
                            onDeleteItem(page._id, 'page');
                          }}
                          okText="Yes"
                          cancelText="No"
                          disabled={!isActionAllowed('delete-page')}
                        >
                          <AntdButton
                            type="link"
                            size="small"
                            icon={deletingLoading === page._id ? <Spin size="small" /> : <FontAwesomeIcon icon={faTrash} />}
                            style={{ color: !isActionAllowed('delete-page') ? '#d9d9d9' : '#ff4d4f' }}
                            disabled={!isActionAllowed('delete-page') || deletingLoading === page._id}
                            loading={deletingLoading === page._id}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Popconfirm>
                      </Tooltip>
                    </Space>
                  </>
                )}
              </div>
            }
          >
            {activePages.includes(page._id) && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 40,
                  alignItems: 'flex-start',
                  marginTop: 16,
                }}
              >
                {page.children?.filter(child => child.bucketLevel === 'dropdown').length > 0 ? (
                  page.children
                    .filter(child => child.bucketLevel === 'dropdown')
                    .map(dropdown => (
                      <div
                        key={dropdown._id}
                        style={{
                          minWidth: 320,
                          maxWidth: 380,
                          flex: '1 1 320px',
                          marginBottom: 16
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '6px',
                          marginBottom: '8px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FontAwesomeIcon icon={faBars} style={{ color: '#1890ff' }} />
                            {editableNode === dropdown._id ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                <InputField
                                  size="small"
                                  value={editingNodeData.name}
                                  onChange={(e) => setEditingNodeData(prev => ({ ...prev, name: e.target.value }))}
                                  placeholder="Dropdown name"
                                  style={{ width: 150 }}
                                />
                                <AntdButton
                                  type="primary"
                                  size="small"
                                  icon={editingLoading === dropdown._id ? <Spin size="small" /> : <FontAwesomeIcon icon={faSave} />}
                                  onClick={() => onSaveNodeEdit(dropdown)}
                                  loading={editingLoading === dropdown._id}
                                />
                                <AntdButton
                                  size="small"
                                  icon={<FontAwesomeIcon icon={faXmark} />}
                                  onClick={onCancelEdit}
                                  disabled={editingLoading === dropdown._id}
                                />
                              </div>
                            ) : (
                              <>
                                <Text
                                  strong
                                  style={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 140,
                                    display: 'inline-block',
                                    verticalAlign: 'middle'
                                  }}
                                >
                                  {dropdown.name}
                                </Text>
                                <Badge count={dropdown.children?.length || 0} showZero style={{ backgroundColor: '#52c41a' }} />

                                {isDeveloperMode && (
                                  <Tooltip title={`Click to copy Dropdown ID: ${dropdown._id}`}>
                                    <Tag
                                      size="small"
                                      color="purple"
                                      style={{
                                        cursor: 'pointer',
                                        fontSize: '10px',
                                        maxWidth: expandedId === dropdown._id ? 'none' : '80px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        transition: 'all 0.3s'
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (expandedId === dropdown._id) {
                                          onCopyToClipboard(dropdown._id, 'Dropdown', e);
                                        } else {
                                          setExpandedId(dropdown._id);
                                        }
                                      }}
                                      icon={copyingLoading === dropdown._id ? <Spin size="small" /> : <FontAwesomeIcon icon={faCopy} />}
                                    >
                                      {copyingLoading === dropdown._id ? 'Copying...' : 
                                       expandedId === dropdown._id ? dropdown._id : dropdown._id.slice(-6)}
                                    </Tag>
                                  </Tooltip>
                                )}
                              </>
                            )}
                          </div>
                          {editableNode === dropdown._id ? null : (
                            <Space>
                              <Tooltip title="Add Option">
                                <AntdButton
                                  type="link"
                                  size="small"
                                  icon={<FontAwesomeIcon icon={faPlus} />}
                                  onClick={() => onOpenModal('option', dropdown._id)}
                                  style={{ color: '#52c41a' }}
                                />
                              </Tooltip>
                              <Tooltip title={!isActionAllowed('edit-dropdown') ? "Developer mode required" : "Edit Dropdown"}>
                                <AntdButton
                                  type="link"
                                  size="small"
                                  icon={<FontAwesomeIcon icon={faPen} />}
                                  onClick={() => onEditNode(dropdown)}
                                  disabled={!isActionAllowed('edit-dropdown')}
                                />
                              </Tooltip>
                              <Tooltip title={!isActionAllowed('delete-dropdown') ? "Developer mode required" : "Delete Dropdown"}>
                                <Popconfirm
                                  title="Delete this dropdown and all its options?"
                                  onConfirm={() => onDeleteItem(dropdown._id, 'dropdown')}
                                  okText="Yes"
                                  cancelText="No"
                                  disabled={!isActionAllowed('delete-dropdown')}
                                >
                                  <AntdButton
                                    type="link"
                                    size="small"
                                    icon={deletingLoading === dropdown._id ? <Spin size="small" /> : <FontAwesomeIcon icon={faTrash} />}
                                    style={{ color: !isActionAllowed('delete-dropdown') ? '#d9d9d9' : '#ff4d4f' }}
                                    disabled={!isActionAllowed('delete-dropdown') || deletingLoading === dropdown._id}
                                    loading={deletingLoading === dropdown._id}
                                  />
                                </Popconfirm>
                              </Tooltip>
                            </Space>
                          )}
                        </div>
                        {dropdown.children && dropdown.children.length > 0 ? (
                          <Tree
                            showLine
                            size="small"
                            expandedKeys={expandedKeys}
                            onExpand={(keys) => setExpandedKeys(keys)}
                            treeData={renderTreeNodes(dropdown.children)}
                            style={{ fontSize: '12px', marginLeft: '10px' }}
                          />
                        ) : (
                          <div style={{
                            textAlign: 'center',
                            padding: '20px',
                            color: '#999',
                            border: '1px dashed #d9d9d9',
                            borderRadius: '6px'
                          }}>
                            <Text type="secondary">No options available</Text>
                            <br />
                            <AntdButton
                              type="link"
                              size="small"
                              onClick={() => onOpenModal('option', dropdown._id)}
                              icon={ <FontAwesomeIcon icon={faPlus} />}
                            >
                              Add first option
                            </AntdButton>
                          </div>
                        )}
                      </div>
                    ))
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div style={{ textAlign: 'center' }}>
                        <p>No dropdowns available</p>
                        {isActionAllowed('create-dropdown') ? (
                          <AntdButton
                            type="primary"
                            size="small"
                            onClick={() => onOpenModal('dropdown', page._id)}
                            label={ "+"}
                          >
                          </AntdButton>
                        ) : (
                          <Text type="secondary">
                            <FontAwesomeIcon icon={faLock} /> Switch to developer mode to add dropdowns
                          </Text>
                        )}
                      </div>
                    }
                  />
                )}
              </div>
            )}
          </Panel>
        ))}
      </Collapse>
    </div>
  );
};

export default DropdownItemList;